<script setup lang="ts">
import { computed } from 'vue'

import { Confirmation } from '@collector/sportsapi-client-legacy'
import { ProbableLinkedIncident } from '@mobile/views/Relation/Shared/RelationDependencies/types'
import { useSportConfiguration } from '@mobile/views/Relation/Shared/RelationSportCommonDependencies/useSportConfiguration'

import { IncidentScoreConfig } from '../RelationSportCommonDependencies/IncidentsResultConfiguration'
import { Result } from './composables'

interface Props {
  linkedIncident: ProbableLinkedIncident
  result: Result | null
  isDeleted?: boolean
}

const props = defineProps<Props>()

const { incidentsResultConfiguration } = useSportConfiguration()

// computed
const isCancelled = computed(
  () => props.linkedIncident.incident.confirmation === Confirmation.Cancelled,
)

const isScoreIncident = computed(() => {
  if (!incidentsResultConfiguration) return false

  const { scoringIncidents, oppositeTeamScoringIncidents } =
    incidentsResultConfiguration
  const incidentId = props.linkedIncident.incident.incident_id
  const attributeIds = props.linkedIncident.incident.attribute_ids
  const scoringIncidentConfig = scoringIncidents.get(incidentId)
  const oppositeTeamScoringIncidentConfig =
    oppositeTeamScoringIncidents?.get(incidentId)

  if (scoringIncidentConfig?.attributes)
    return attributeIds.some((attributeId) =>
      isAttributeScoreAttribute(attributeId, scoringIncidentConfig),
    )
  if (oppositeTeamScoringIncidentConfig?.attributes)
    return attributeIds.some((attributeId) =>
      isAttributeScoreAttribute(attributeId, oppositeTeamScoringIncidentConfig),
    )
  return (
    !!scoringIncidentConfig?.visible ||
    !!oppositeTeamScoringIncidentConfig?.visible
  )
})

const resultText = computed((): string => {
  if (!incidentsResultConfiguration) return ''
  if (!isScoreIncident.value) return ''
  if (isCancelled.value) return ''
  if (props.isDeleted) return ''
  return `${props.result?.firstTeamResult} : ${props.result?.secondTeamResult}`
})

const shouldShowResult = computed(
  () =>
    !!isScoreIncident.value &&
    !isCancelled.value &&
    !props.isDeleted &&
    !!props.result,
)

// methods
function isAttributeScoreAttribute(
  attributeId: number,
  scoreConfig: IncidentScoreConfig,
): boolean {
  const config = scoreConfig.attributes?.get(attributeId)
  return !!config?.visible && !!config.add
}
</script>

<template>
  <span
    v-if="shouldShowResult"
    class="text-2xs ml-1 font-bold"
  >
    ({{ resultText }})
  </span>
</template>
